/* DependencyInjector.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Registry = require("./Registry");
const _ = require("lodash");

class DependencyInjector extends EventEmitter {
    constructor(parentInjector) {
        super();
        this._registry = new Registry();
        if (
            parentInjector &&
            !Object.prototype.isPrototypeOf.call(DependencyInjector.prototype, parentInjector)
        ) {
            throw new Error(
                "Cannot create a Dependency Injector with a parent that is not of type DependencyInjector"
            );
        }
        this._parentInjector = parentInjector;
        this._childInjectors = [];
    }

    /******************************************/
    /* START DI METHODS */

    /******************************************/
    /**
     * Get all the registered objects via namespace using a glob, or an array of globs.
     * If its not found locally it will crawl the DI ancestors and look for it there.
     * @param {string|string[]} [namespaceGlob]
     * @returns {RegistryCache} All the currently registered objects that match the glob.
     * @throws {Error} If the provided arguments are invalid
     */
    getAllRegisteredByGlob(glob) {
        let localResults = this._registry.getAllRegisteredByGlob(glob);
        let ancestorResults = {};

        if (this._parentInjector) {
            ancestorResults = this._parentInjector.getAllRegisteredByGlob(glob);
        }
        //merge them without messing up the strict equality
        Object.keys(ancestorResults).forEach((ancestorNamespace) => {
            if (!Object.hasOwn(localResults, ancestorNamespace)) {
                localResults[ancestorNamespace] = {};
            }
            Object.keys(ancestorResults[ancestorNamespace]).forEach((ancestorName) => {
                //if the local registry does NOT have this thing registered take the ancestor's
                if (!Object.hasOwn(localResults[ancestorNamespace], ancestorName)) {
                    localResults[ancestorNamespace][ancestorName] =
                        ancestorResults[ancestorNamespace][ancestorName];
                }
            });
        });
        return localResults;
    }

    /**
     * Get the currently registered object by namespace and name.
     * If its not found locally it will crawl the DI ancestors and look for it there.
     * @param {string|string[]} namespace - The dot notation namespace of the object to retrieve, or an array of namespaces in order of preference.
     * @param {string} name - The unique name of the object to retrieve.
     * @returns {*} The object that is currently registered to that namespace and name or null if NOT found.
     * @throws {Error} If the provided arguments are invalid
     */
    getRegistered(/*namespace, name*/) {
        //jshint ignore:line
        /* jshint -W080 */
        let found = null;
        let injected = this._registry.getRegistered.apply(this._registry, arguments);
        if (injected !== null) {
            found = injected;
        } else {
            if (this._parentInjector) {
                let injectedByParent = this._parentInjector.getRegistered.apply(
                    this._parentInjector,
                    arguments
                );
                if (injectedByParent !== null) {
                    found = injectedByParent;
                }
            }
        }
        return found;
    }

    isRegistered(/*namespace, name*/) {
        //jshint ignore:line
        let found = this.getRegistered.apply(this, arguments);
        return !!found;
    }

    /**
     * Retrieve an Injectable from this Dependency Injector or one of its Ancestors.
     * @param {string} injectableFullyQualifiedName - The fully qualified name (<namespace>.<name>) of the injectable to locate.
     * @param {Object.<String,*>} [overrides={}] - An object used to override any previously registered injectables. This Object's Keys
     *      are the fully qualified name (<namespace>.<name>) of the injectable, it's Values are the injectable to use instead of the previously registered one.
     * @returns {*} The found injectable or null if nothing is found.
     */
    getInjectable(injectableFullyQualifiedName, overrides) {
        let localOverrides = overrides || {};
        let found;
        if (Object.hasOwn(localOverrides, injectableFullyQualifiedName)) {
            found = localOverrides[injectableFullyQualifiedName];
        } else {
            found = this.getRegistered(injectableFullyQualifiedName);
            if (found) {
                found = found.registeredObj;
            }
        }
        return found;
    }

    /**
     *
     * @param {Function|Class} klass - The newable Function or class to create an instance from.
     * @param {Object} [opts] - The optional parameters used in the creation.
     * @param {Array.<*>} [opts.defaultInjectables=[]] - The default injectables to provide to the constructor function if no $inject is provided.
     * @param {Object.<String,*>} [opts.injectableOverrides={}] - An object used to override any previously registered injectables. This Object's Keys
     *      are the fully qualified name (<namespace>.<name>) of the injectable, it's Values are the injectable to use instead of the previously registered one.
     *
     * @returns {ClassInstance} The created instance.
     * @throws {Error} If bad params were passed in or an Injectable cannot be found.
     */
    create(klass, opts) {
        if (!_.isFunction(klass)) {
            throw new Error("Cannot create an instance of a non function (" + klass.name + ").");
        }
        let defaultInjectables = _.get(opts, "defaultInjectables", ["$teve.di"]);
        let localOverrides = _.get(opts, "injectableOverrides", {});

        //if this klass is not calling for any specific injectables provide the defaults.
        if (!_.has(klass, "$inject")) {
            klass.$inject = defaultInjectables;
        }

        let args = [];
        //loop through the injectables and first try and find them in the localOverrides
        //if they are not found there look in the Registry
        //if they are not found there start looking in the Parent Injectors
        klass.$inject.forEach((injectableDetails) => {
            if (!_.isString(injectableDetails)) {
                throw new Error(
                    "$inject must be an array of strings, you provided the non-string (" +
                        JSON.stringify(injectableDetails) +
                        ") when attempting to create class (" +
                        klass.name +
                        ")."
                );
            }
            let injectable;
            if (injectableDetails.toLowerCase() === "$teve.di") {
                injectable = this;
            } else {
                injectable = this.getInjectable(injectableDetails, localOverrides);
            }

            if (injectable) {
                args.push(injectable);
            } else {
                throw new Error(
                    `Cannot find a previously cached Injectable (${injectableDetails}) for class (${klass}).`
                );
            }
        });
        args.unshift(klass);

        let constructorFn = klass.bind.apply(klass, args);
        let created = new constructorFn();
        //Expose the di and Registered Details to ALL Injected things
        _.set(created, "$teve.di", this);
        if (_.get(opts, "injectableOverrides.$teve.registeredDetails")) {
            let registrationDetails = opts.injectableOverrides.$teve.registeredDetails;
            registrationDetails.fqn = this._registry.getFullyQualifiedName(
                opts.injectableOverrides.$teve.registeredDetails.namespace,
                opts.injectableOverrides.$teve.registeredDetails.name
            );
            _.set(created, "$teve.registeredDetails", registrationDetails);
        }
        return created;
    }

    /**
     * Create an Instance of a class stored in this Dependency Injector or one of the Ancestors.
     * @param {string|string[]} namespace - The dot notation namespace or an array of namespaces to search for the class to create the instance from.
     * @param {string} name - The unique name of the class to create the instance from.
     * @param {Array.<*>} [opts.defaultInjectables=[]] - The default injectables to provide to the constructor function if no $inject is provided.
     * @param {Object.<String,*>} [opts.injectableOverrides={}] - An object used to override any previously registered injectables. This Object's Keys
     *      are the fully qualified name (<namespace>.<name>) of the injectable, it's Values are the injectable to use instead of the previously registered one.
     *
     * @returns {ClassInstance} The created instance.
     * @throws {Error} If bad params were passed in or an Injectable cannot be found.
     */
    createInstance(namespace, name, opts) {
        let namingArgs = Array.from(arguments);
        //If the last argument is NOT a string
        if (!_.isString(arguments[arguments.length - 1])) {
            namingArgs = namingArgs.slice(0, -1);
            opts = arguments[arguments.length - 1];
        }
        let registered = this.getRegistered.apply(this, namingArgs);
        let foundKlass = _.get(registered, "registeredObj", null);
        if (foundKlass !== null) {
            if (_.isFunction(foundKlass)) {
                //allow the user to get access to the registeredDetails via DI
                let options = _.merge({}, opts, {
                    injectableOverrides: {
                        "$teve.registeredDetails": registered.registeredDetails,
                    },
                });
                return this.create(foundKlass, options);
            } else {
                throw new Error(
                    `The class we found to create your instance from, (${foundKlass}) MUST be a constructor function..`
                );
            }
        } else {
            throw new Error(
                `Cannot find a class to create your instance from, using fully qualified name (${this._registry.getFullyQualifiedName(
                    namespace,
                    name
                )}).`
            );
        }
    }

    /**
     * Creates a Child Dependency Injector.
     * @returns {DependencyInjector} The created
     */
    createDependencyInjector() {
        let child = new DependencyInjector(this);
        this._childInjectors.push(child);
        return child;
    }

    removeChild(childInjector) {
        let idx = this._childInjectors.indexOf(childInjector);
        if (idx !== -1) {
            this._childInjectors.splice(idx, 1);
        }
    }

    /******************************************/
    /* END DI METHODS */
    /* START REGISTRY METHODS */

    /******************************************/
    get registry() {
        return this.getRegistry();
    }

    getRegistry() {
        return this._registry;
    }

    /******************************************/
    /* END REGISTRY METHODS */
    /* START DEPENDENCY INJECTOR METHODS */

    /******************************************/
    isRoot() {
        return this.parentInjector === undefined;
    }

    get childInjectors() {
        return this.getChildInjectors();
    }

    getChildInjectors() {
        return this._childInjectors;
    }

    get parentInjector() {
        return this.getParentInjector();
    }

    getParentInjector() {
        return this._parentInjector;
    }

    /**
     * Shuts down this Dependency Injector and all the registered objects.
     * It will also shutdown all the descendant Injectors and their registered objects.
     * @returns {Promise.<*>}
     */
    shutdown() {
        let promises = [];
        promises.push(this._registry.shutdown());

        //if child Injectors shut all of them down
        this._childInjectors.forEach((injector) => {
            promises.push(injector.shutdown());
        });

        if (this._parentInjector) {
            this._parentInjector.removeChild(this);
        }

        let allProms = Promise.all(promises);
        allProms.then(() => {
            this._parentInjector = undefined;
            this._registry = undefined;
            this._childInjectors = undefined;
            this.emit("ShutdownComplete");
        });
        return allProms;
    }

    /******************************************/
    /* END DEPENDENCY INJECTOR METHODS */
    /******************************************/
}

module.exports = DependencyInjector;
